import React from 'react';
import { Card, CardContent } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Image as ImageIcon, LayoutTemplate, UploadCloud, Image as ImagePlaceholder, Check, Phone, Loader2 } from "lucide-react";
import { AuditTrailModal } from "./AuditTrailModal";
import { useAddSelfServieBranding, useUpdateSelfServieBranding } from '../../hooks';

const colors = [
  { name: 'Ocean Blue', value: 'bg-gradient-to-br from-[#3b5bff] to-[#6a2cff]' },
  { name: 'Violet Pink', value: 'bg-gradient-to-br from-[#b57bff] to-[#ff6bd6]' },
  { name: 'Teal Sky', value: 'bg-gradient-to-br from-[#22a6d6] to-[#3be0c4]' },
  { name: 'Sunset', value: 'bg-gradient-to-br from-[#ff8a3d] to-[#ff5b7a]' },
  { name: 'Forest', value: 'bg-gradient-to-br from-[#1fa765] to-[#0e7490]' },
  { name: 'Navy Solid', value: 'bg-[#0f1f3d]' },
];

const LoginPageBranding = ({ mode, setMode, bgColor, setBgColor, customImageFile, setCustomImageFile, portalMode, portalText, portalLogo, onReset, remoteUtilityId, isPristine, existingId, existingBgUrl, portalExistingLogoUrl }: any) => {
  const { mutate: addBranding, isPending: isAdding } = useAddSelfServieBranding();
  const { mutate: updateBranding, isPending: isUpdating } = useUpdateSelfServieBranding();

  const isPending = isAdding || isUpdating;
  const [isEditing, setIsEditing] = React.useState(!existingId);

  React.useEffect(() => {
    setIsEditing(!existingId);
  }, [existingId]);

  const handleSave = () => {
    if (mode === 'default') {
      const presetMap: any = {
        'Ocean Blue': 'ocean_blue',
        'Violet Pink': 'violet_pink',
        'Teal Sky': 'teal_sky',
        'Sunset': 'sunset',
        'Forest': 'forest',
        'Navy Solid': 'navy_solid'
      };
      
      const payload = {
        branding_type: 0,
        branding_field_type: 2,
        preset: presetMap[bgColor] || 'ocean_blue',
        remote_utility_id: remoteUtilityId
      };
      if (existingId) {
        updateBranding({ id: existingId, payload });
      } else {
        addBranding(payload);
      }
    } else {
      const formData = new FormData();
      formData.append('branding_type', '0');
      formData.append('branding_field_type', '3');
      formData.append('remote_utility_id', remoteUtilityId);
      if (customImageFile) {
        formData.append('background_image', customImageFile);
      }
      
      if (existingId) {
        updateBranding({ id: existingId, payload: formData });
      } else {
        addBranding(formData);
      }
    }
  };

  const selectedColorClass = colors.find(c => c.name === bgColor)?.value || colors[0].value;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
      {/* Settings Column */}
      <Card className="shadow-sm border-gray-200">
        <CardContent className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Login Page Branding</h2>
          <p className="text-sm text-gray-500 mb-6">
            Pick a built-in background theme for the login page or upload your own image.
          </p>

          <div className={!isEditing ? 'pointer-events-none opacity-60' : ''}>
            <div className="grid grid-cols-2 gap-4 mb-6">
            <div 
              className={`border rounded-lg p-4 cursor-pointer flex items-center gap-3 transition-colors ${mode === 'default' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              onClick={() => setMode('default')}
            >
              <div className={`p-2 rounded bg-blue-500 text-white`}>
                <LayoutTemplate className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-gray-900">Default Background</div>
                <div className="text-[10px] text-gray-500">Choose from built-in themes.</div>
              </div>
              <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${mode === 'default' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                {mode === 'default' && <Check className="w-3 h-3 text-white" />}
              </div>
            </div>

            <div 
              className={`border rounded-lg p-4 cursor-pointer flex items-center gap-3 transition-colors ${mode === 'custom' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              onClick={() => setMode('custom')}
            >
              <div className={`p-2 rounded bg-blue-500 text-white`}>
                <ImageIcon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-gray-900">Custom Image</div>
                <div className="text-[10px] text-gray-500">Upload a PNG, JPG, or WebP (up to 3 MB).</div>
              </div>
              <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${mode === 'custom' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                {mode === 'custom' && <Check className="w-3 h-3 text-white" />}
              </div>
            </div>
          </div>

          {mode === 'default' && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              {colors.map(color => (
                <div 
                  key={color.name}
                  className={`border rounded-lg overflow-hidden cursor-pointer ${bgColor === color.name ? 'border-blue-500 ring-2 ring-blue-500 ring-offset-1' : 'border-gray-200 hover:border-gray-300'}`}
                  onClick={() => setBgColor(color.name)}
                >
                  <div className={`h-14 w-full ${color.value}`}></div>
                  <div className="p-2.5 flex items-center justify-between bg-white border-t border-gray-100">
                    <span className="text-xs font-semibold text-gray-700">{color.name}</span>
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${bgColor === color.name ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                      {bgColor === color.name && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {mode === 'custom' && !customImageFile && (
            <label htmlFor="login-bg-upload" className="border border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center bg-gray-50/50 mb-6 transition-all hover:bg-gray-50 cursor-pointer text-center w-full block">
              <input type="file" id="login-bg-upload" className="hidden" accept="image/png, image/jpeg, image/webp" onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; if (!["image/png","image/jpeg","image/webp"].includes(f.type)) { e.target.value = ""; return; } setCustomImageFile(f); }} />
              <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mb-3 mx-auto">
                <UploadCloud className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-sm font-bold text-gray-900 mb-1">Click to upload image</p>
              <p className="text-xs text-gray-500">PNG, JPG, or WebP up to 3 MB</p>
            </label>
          )}

          {mode === 'custom' && customImageFile && (
            <div className="border border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center bg-gray-50/50 mb-6 w-full">
              <div className="w-full flex items-center justify-between">
                <div className="flex items-center gap-3">
                   <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
                     <ImagePlaceholder className="w-6 h-6" />
                   </div>
                   <div>
                     <p className="text-sm font-bold text-gray-900 truncate max-w-[200px]">{customImageFile.name}</p>
                     <p className="text-xs text-gray-500">{(customImageFile.size / 1024).toFixed(1)} KB</p>
                   </div>
                </div>
                <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 text-sm" onClick={() => setCustomImageFile(null)}>
                  Remove
                </Button>
              </div>
            </div>
          )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <AuditTrailModal 
              title="Login Page Branding" 
              remoteUtilityId={remoteUtilityId} 
              actionType="LOGIN_BRANDING_UPDATE" 
            />

            <Button variant="outline" className="text-gray-600 gap-2" onClick={!isEditing ? () => setIsEditing(true) : onReset} disabled={isPending}>
              {!isEditing ? "Edit" : "Reset"}
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={handleSave} disabled={isPending || isPristine || !isEditing}>
              {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {isPending ? "Saving..." : "Save Login Branding"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Column */}
      <Card className="shadow-sm border-gray-200 h-full">
        <CardContent className="p-6 h-full flex flex-col">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Live Preview</h2>
          <p className="text-sm text-gray-500 mb-6">Preview of the consumer login page with the selected background.</p>
          
          <div className="w-full bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col flex-1">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-4 py-3 border-b border-gray-100 shrink-0">
              CONSUMER LOGIN PAGE
            </div>
            
            {/* The Login Page View */}
            <div 
              className={`flex-1 flex items-center justify-center p-8 transition-all duration-300 min-h-[300px] ${mode === 'default' ? selectedColorClass : (customImageFile || existingBgUrl ? 'bg-cover bg-center' : 'bg-gray-200')}`}
              style={mode === 'custom' && (customImageFile || existingBgUrl) ? { backgroundImage: `url(${customImageFile ? URL.createObjectURL(customImageFile) : existingBgUrl})` } : {}}
            >
               <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-[280px] flex flex-col items-center border border-gray-100">
                 {portalMode === 'logo' && (portalLogo || portalExistingLogoUrl) ? (
                   <div className="w-auto h-12 bg-white rounded flex items-center justify-center mb-6">
                     <img src={portalLogo ? URL.createObjectURL(portalLogo) : portalExistingLogoUrl} alt="Logo" className="max-h-12 max-w-full object-contain" />
                   </div>
                 ) : (
                   <div className="text-lg font-extrabold text-gray-900 mb-6 text-center truncate w-full">
                     {portalMode === 'text' && portalText ? portalText : 'Your Logo'}
                   </div>
                 )}
                 
                 <div className="w-full space-y-3">
                   <div>
                     <div className="text-[10px] font-semibold text-gray-500 mb-1 ml-0.5">Email</div>
                     <div className="h-8 w-full bg-gray-50 border border-gray-200 rounded"></div>
                   </div>
                   <div>
                     <div className="text-[10px] font-semibold text-gray-500 mb-1 ml-0.5">Password</div>
                     <div className="h-8 w-full bg-gray-50 border border-gray-200 rounded"></div>
                   </div>
                   <div className="h-9 w-full bg-blue-600 rounded mt-4 flex items-center justify-center text-white text-sm font-semibold shadow-sm shadow-blue-600/30">
                     Sign In
                   </div>
                 </div>
               </div>
            </div>

          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPageBranding;
